import { getFeatureWithId } from "../utils/mapUtils";
import { FcCheckmark, FcCancel } from "react-icons/fc";
import { Button, ButtonGroup } from "@progress/kendo-react-buttons";
import { Tooltip } from "@progress/kendo-react-tooltip";
import { Point } from "ol/geom.js";
import Feature from "ol/Feature.js";
import React from "react";
import { filterReadFields, updateEditFields } from "../../permissionUtils";
import { formatFieldValue } from "../SideBar/Results/Identify/FeatureDescription";
import { makeArray } from "../../permissionUtils";

// Created data for grid and excel export
//   when createSelection is true,
//    creates object with featureIds and booleans indicating selected state
export const createGridData = (map, results, vectorFields, createSelection = false) => {
    if (createSelection === true) {
        const newGridData = [];
        const newSelectedState = {};
        results.Features.forEach((featureId) => {
            let f = getFeatureWithId(map, results.Id, featureId);
            if (f != null) {
                const { geometry, ...props } = f.values_;
                const gridObj = { ...props, featureId: featureId };
                const formattedGridObj = formatGridValues(gridObj, vectorFields);
                newGridData.push(formattedGridObj);
                Object.assign(newSelectedState, { [featureId]: false });
            }
        });
        return [newGridData, newSelectedState];
    } else {
        const newGridData = [];
        results.Features.forEach((featureId) => {
            let f = getFeatureWithId(map, results.Id, featureId);
            if (f != null) {
                const { geometry, ...props } = f.values_;
                const gridObj = { ...props, featureId: featureId };
                const formattedGridObj = formatGridValues(gridObj, vectorFields);
                newGridData.push(formattedGridObj);
            }
        });
        return newGridData;
    }
};

/**
 * Formats each row in grid using field and formatting function
 * @param {object} gridObj Values from feature from map (row in grid)
 * @param {object} vectorFields Field for layer from redux
 * @returns Object with formatted values (row in grid)
 */
export const formatGridValues = (gridObj, vectorFields) => {
    const formattedGridObj = {};
    for (const [key, value] of Object.entries(gridObj)) {
        const fieldObj = makeArray(vectorFields?.Field).find(
            (f) => f?.Name?.toLowerCase() === key?.toLowerCase()
        );
        // Handle possible undefined values or values not in feature but not in fields
        if (fieldObj === undefined) {
            Object.assign(formattedGridObj, { [key]: value });
        } else {
            const displayValue = formatFieldValue(fieldObj, gridObj);
            Object.assign(formattedGridObj, { [key]: displayValue });
        }
    }
    return formattedGridObj;
};

// Takes vectorLayers any[] from redux, layerId from redux and permissions any[] from redux
export const createFieldsArray = (vectorLayers, layerId, permissions) => {
    const vector = vectorLayers.find((v) => v.Id === layerId);
    const fieldArr = vector.Fields.Field;
    const fields = Array.isArray(fieldArr) ? fieldArr : [fieldArr];
    const readFields = filterReadFields(fields, permissions, layerId);
    const editFields = updateEditFields(readFields, permissions, vector);
    return editFields;
};

export const updateItem = (item, data) => {
    let index = data.findIndex((record) => record.featureId === item.featureId);
    data[index] = item;
    return data;
};

// Compenent returning buttons for editing
export const ButtonCell = (props) => {
    const { dataItem } = props;
    const inEdit = dataItem["inEdit"];
    return inEdit === true ? (
        <Tooltip anchorElement="target" position="top" parentTitle={true}>
            <ButtonGroup className="grid-edit-buttons">
                <Button title="Save" onClick={() => props.update(dataItem)}>
                    <FcCheckmark style={{ width: "20px", height: "100%" }} />
                </Button>
                <Button title="Cancel" onClick={() => props.cancel(dataItem)}>
                    <FcCancel style={{ width: "20px", height: "100%" }} />
                </Button>
            </ButtonGroup>
        </Tooltip>
    ) : (
        <Button onClick={() => props.enterEdit(dataItem)}>
            <div>Edit</div>
        </Button>
    );
};

export const createGeocodeFeatures = (results, geocodingData) => {
    const features = [];
    results.Features.forEach((feat) => {
        const result = geocodingData.candidates.find(
            (c) => c.attributes.Match_addr === feat
        );
        // return if no result found
        if (!result) {
            return;
        }
        // create feature to add to map
        const feature = new Feature({
            geometry: new Point([result.location.x, result.location.y]),
            Id: `${result.location.x},${result.location.y}`,
            attributes: result.attributes,
        });
        feature.setId(`${result.location.x},${result.location.y}`);
        features.push(feature);
    });
    return features;
};

export const createGeocodeData = (results, geocodingData) => {
    const geoSelected = {};
    const geoFields = [
        { DisplayName: "Address", Name: "Address" },
        { DisplayName: "Score", Name: "Score" },
    ];
    const geoGridData = [];

    results.Features.forEach((feat) => {
        const r = geocodingData.candidates.find((c) => c.attributes.Match_addr === feat);
        if (!r) return;
        Object.assign(geoSelected, {
            [`${r.location.x},${r.location.y}`]: false,
        });
        geoGridData.push({
            Address: r.address,
            Score: r.score,
            featureId: `${r.location.x},${r.location.y}`,
        });
    });
    return [geoGridData, geoFields, geoSelected];
};

export const RowRender = (props) => {
    const dataItem = props.originalProps;
    const trProps = {
        ...props.tr.props,
        onBlur: () => {
            props.exitEdit();
        },
        onKeyDown: (e) => {
            if (e.key === "Enter") {
                props.exitEdit();
            }
        },
        onMouseEnter: () => {
            props.onHoverStart(dataItem);
        },
        onMouseLeave: () => {
            props.onHoverEnd(dataItem);
        },
    };
    return React.cloneElement(
        props.tr,
        {
            ...trProps,
        },
        props.tr.props.children
    );
};

export const CellRender = (props) => {
    const dataItem = props.originalProps.dataItem;
    const cellField = props.originalProps.field;
    const inEditField = dataItem[props.editField || ""];
    const additionalProps =
        cellField && cellField === inEditField
            ? {
                  ref: (td) => {
                      const input = td && td.querySelector("input");
                      const activeElement = document.activeElement;
                      if (
                          !input ||
                          !activeElement ||
                          input === activeElement ||
                          !activeElement.contains(input)
                      ) {
                          return;
                      }
                      input.select();
                  },
              }
            : {
                  onClick: () => {
                      props.enterEdit(dataItem, cellField);
                  },
              };
    const clonedProps =
        props.td !== null
            ? {
                  ...props.td.props,
                  ...additionalProps,
              }
            : { ...additionalProps };
    return props.td !== null
        ? React.cloneElement(props.td, clonedProps, props.td.props.children)
        : React.cloneElement(<td></td>, clonedProps, "");
};
